import { Modal } from "./Modal.tsx";
import clsx from "clsx";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { helpers } from "../util/helpers.ts";
import { showNotification } from "../util/showNotification.ts";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";
import { HelpPopover } from "../components/HelpPopover.tsx";
import { RatingsStatsPopover } from "../components/RatingsStatsPopover/index.tsx";
import { Mood } from "../components/Mood.tsx";
import { PlayerPicture } from "../components/PlayerPicture.tsx";
import { useRef, useState } from "react";
import type api from "../../worker/api/index.ts";
import { showSignUndo } from "./NegotiateButtons.tsx";

type NegotaitionModalProps = Exclude<
	Awaited<ReturnType<typeof api.main.getNegotiationProps>>,
	string
>;

const SignButton = ({
	p,
	amount,
	exp,
	disabledReason,
	onSuccess,
}: {
	p: { name: string; pid: number };
	amount: number;
	exp: number;
	disabledReason: string | undefined;
	onSuccess: () => void;
}) => {
	const button = (
		<button
			className={`btn ${disabledReason !== undefined ? "btn-secondary" : "btn-success"}`}
			disabled={disabledReason !== undefined}
			onClick={async () => {
				const response = await toWorker("main", "acceptContractNegotiation", {
					pid: p.pid,
					amount: Math.round(amount * 1000),
					exp,
				});
				if (typeof response === "string") {
					showNotification({
						type: "error",
						text: response,
					});
				} else {
					onSuccess();
					showSignUndo({
						name: p.name,
						undoKey: response,
					});
				}
			}}
		>
			Sign
			<span className="d-none d-sm-inline"> contract</span>
		</button>
	);

	if (disabledReason === undefined) {
		return button;
	}

	// CSS/HTML hacks!
	// position-fixed is for https://stackoverflow.com/a/75264190/786644 https://github.com/react-bootstrap/react-bootstrap/issues/6563 otherwise the scrollback flicker appears/disappears on desktop when the react-bootstrap Tooltip is shown. However this breaks if you scroll, but this page doesn't need to scroll.
	// Wrapper div around button is because otherwise there is no hover over the disabled button and no tooltip is shown.
	return (
		<OverlayTrigger
			overlay={<Tooltip className="position-fixed">{disabledReason}</Tooltip>}
		>
			<div>{button}</div>
		</OverlayTrigger>
	);
};

const widthStyle = { maxWidth: 575 };

const NegotiationHeader = ({
	capSpace,
	challengeNoRatings,
	payroll,
	p,
	resigning,
	salaryCap,
	salaryCapType,
	t,
}: Pick<
	NegotaitionModalProps,
	| "capSpace"
	| "challengeNoRatings"
	| "payroll"
	| "p"
	| "resigning"
	| "salaryCap"
	| "salaryCapType"
	| "t"
>) => {
	const { gender } = useLocal(["gender"]);

	let message;
	if (salaryCapType === "soft") {
		if (resigning) {
			message = (
				<>
					You are allowed to go over the salary cap when re-signing players.{" "}
					<b>
						If you do not come to an agreement here, {p.name} will become a free
						agent.
					</b>{" "}
					{helpers.pronoun(gender, "He")} will then be able to sign with any
					team, and you won't be able to go over the salary cap to sign{" "}
					{helpers.pronoun(gender, "him")} unless{" "}
					{helpers.pronoun(gender, "he")}'s asking for a minimum contract.
				</>
			);
		} else {
			message =
				"You are not allowed to go over the salary cap to sign free agents, unless it's for a minimum contract.";
		}
	} else if (salaryCapType === "hard") {
		message =
			"You are not allowed to go over the salary cap to sign players, unless it's for a minimum contract.";
	}

	return (
		<>
			<div className="d-flex gap-2 mb-3" style={widthStyle}>
				<div
					style={{
						height: 90,
						width: 60,
						marginTop: p.imgURL ? 0 : -10,
					}}
					className="flex-shrink-0 d-flex justify-content-center align-items-center"
				>
					<PlayerPicture
						face={p.face}
						imgURL={p.imgURL}
						colors={t.colors}
						jersey={t.jersey}
						lazy
					/>
				</div>
				<div className="d-flex flex-column justify-content-end">
					<div className="d-flex flex-wrap gap-2">
						<h1 className="mb-0 text-nowrap">
							<a href={helpers.leagueUrl(["player", p.pid])}>{p.name}</a>
						</h1>
						<div className="d-flex align-items-center">
							<Mood defaultType="user" p={p} />
							<RatingsStatsPopover pid={p.pid} defaultWatch={p.watch} />
						</div>
					</div>
					<div>
						{p.age} years old
						{!challengeNoRatings
							? `; Overall: ${p.ratings.ovr}; Potential: ${p.ratings.pot}`
							: null}
					</div>
					<div>
						{resigning ? "Re-signing" : "Free Agent"}
						{message ? (
							<HelpPopover className="ms-1">{message}</HelpPopover>
						) : null}
					</div>
				</div>
				<div className="ms-auto d-none d-sm-flex flex-column justify-content-end align-items-end text-nowrap">
					{salaryCapType !== "none" ? (
						<>
							<div>Salary Cap: {helpers.formatCurrency(salaryCap, "M")}</div>
							<div>Payroll: {helpers.formatCurrency(payroll, "M")}</div>
							<div>Cap Space: {helpers.formatCurrency(capSpace, "M")}</div>
						</>
					) : (
						<div>Payroll: {helpers.formatCurrency(payroll, "M")}</div>
					)}
				</div>
			</div>
		</>
	);
};

const PresetNegotiation = ({
	contractOptions,
	close,
	p,
}: Pick<NegotaitionModalProps, "contractOptions" | "p"> & {
	close: () => void;
}) => {
	return (
		<>
			<div className="list-group" style={widthStyle}>
				{contractOptions.map((contract, i) => {
					return (
						<div
							key={i}
							className={clsx("d-flex align-items-center list-group-item", {
								"list-group-item-success": contract.smallestAmount,
							})}
						>
							<div className="flex-grow-1">
								<b>{helpers.formatCurrency(contract.amount, "M")}</b>/year for{" "}
								{contract.years} {helpers.plural("year", contract.years)}{" "}
								(through {contract.exp})
							</div>

							<SignButton
								p={p}
								amount={contract.amount}
								exp={contract.exp}
								disabledReason={contract.disabledReason}
								onSuccess={close}
							/>
						</div>
					);
				})}
			</div>
		</>
	);
};

const Negotiation = ({ contractOptions, close, p, resigning, market, offerLimits, pendingOffer }: Pick<NegotaitionModalProps, "contractOptions" | "p" | "resigning" | "market" | "offerLimits" | "pendingOffer"> & { close: () => void }) => {
 const first = contractOptions.find(c => c.smallestAmount) ?? contractOptions[0];
 const [amount, setAmount] = useState(pendingOffer ? String(pendingOffer.amount / 1000) : "");
 const [exp, setExp] = useState(pendingOffer?.exp ?? first?.exp ?? 0);
 const [message, setMessage] = useState("");
 const [busy, setBusy] = useState(false);
 if (resigning) return <>
  {market.length > 0 ? <div className="alert alert-warning" style={widthStyle}><b>Other teams want him</b><ul className="mb-1">{market.map(b=><li key={b.tid}>{b.name}: {helpers.formatCurrency(b.amount/1000,"M")}/yr thru {b.exp}</li>)}</ul>The contracts below already match what it takes to keep him. His mood toward your team lowers his price, especially if he's loyal.</div> : null}
  <PresetNegotiation contractOptions={contractOptions} close={close} p={p} />
 </>;
 return <form style={widthStyle} onSubmit={async e => {
  e.preventDefault(); if (busy) return; setBusy(true); setMessage("");
  try {
   const result = await toWorker("main", "acceptContractNegotiation", {pid:p.pid,amount:Math.round(Number(amount)*1000),exp,quoteOnly:amount.trim()===""});
   if (typeof result === "string") setMessage(result);
   else { close(); showSignUndo({name:p.name,undoKey:result}); }
  } catch {setMessage("Couldn't send the offer. Please try again.");} finally {setBusy(false);}
 }}>
 <h2 className="h5">Negotiate contract</h2>
 <p>Money, contract length and the player's preferences decide his answer. Leave the salary empty to check what he asks for the chosen length, without making an offer. An offer that covers what he asks signs him right away.</p>
 {pendingOffer && <div className="alert alert-success">Offer sent: {helpers.formatCurrency(pendingOffer.amount / 1000, "M")}/yr thru {pendingOffer.exp}. He decides in {pendingOffer.daysLeft === 1 ? "1 day" : `${pendingOffer.daysLeft} days`}. Sending a new offer restarts the clock. <a href={`${helpers.leagueUrl(["free_agents"])}#negotiations`}>View negotiations</a></div>}
 <div className="row g-2 mb-3">
 <label className="col-7">Annual salary (millions)<input className="form-control" type="number" inputMode="decimal" min={offerLimits.min} max={offerLimits.max} step="0.001" placeholder="Optional, leave empty to check" value={amount} onChange={e=>setAmount(e.target.value)} disabled={busy}/></label>
 <label className="col-5">Years<input className="form-control" type="number" inputMode="numeric" min={offerLimits.minYears} max={offerLimits.maxYears} step="1" required value={exp-offerLimits.baseYear} onChange={e=>setExp(Number(e.target.value)+offerLimits.baseYear)} disabled={busy}/></label>
 </div>
 <p className="small text-body-secondary">Starting point: {helpers.formatCurrency(first?.amount ?? 0,"M")}/yr for {first?.years} year(s). He may ask for a different amount for the length you pick.</p>
 {message && <div role="status" className="alert alert-info">{message}</div>}
 <button className="btn btn-success w-100" disabled={busy || !first}>{busy ? "Checking…" : amount.trim()==="" ? "Check asking price" : pendingOffer ? "Change offer" : "Send offer"}</button>
 </form>;
};

export const NegotiationModal = ({
	close,
	negotiationProps,
	show,
}: {
	close: () => void;
	negotiationProps: NegotaitionModalProps | undefined;
	show: boolean;
}) => {
	return (
		<Modal show={show} onHide={close}>
			<Modal.Body>
				<button
					type="button"
					className="btn-close"
					aria-label="Close"
					onClick={close}
					style={{
						position: "absolute",
						top: 16,
						right: 16,
					}}
				></button>
				{negotiationProps ? (
					<>
						<NegotiationHeader {...negotiationProps} />
						<Negotiation key={negotiationProps.p.pid} {...negotiationProps} close={close} />
					</>
				) : null}
			</Modal.Body>
		</Modal>
	);
};

export const useNegotiaionModal = () => {
	const [state, setState] = useState<
		| {
				status: "loaded";
				props: NegotaitionModalProps;
				show: boolean;
		  }
		| {
				status: "init";
		  }
	>({
		status: "init",
	});

	const show = state.status === "loaded" && state.show;

	const loadingPid = useRef<number | undefined>(undefined);

	const props = {
		close: () => {
			if (show) {
				setState((state) => {
					if (state.status === "loaded") {
						return { ...state, show: false };
					}

					return state;
				});
			}
		},
		negotiationProps: state.status === "loaded" ? state.props : undefined,
		show,
	};

	return {
		negotiate: async (pid: number) => {
			loadingPid.current = pid;

			const newProps = await toWorker("main", "getNegotiationProps", pid);

			if (loadingPid.current !== pid) {
				// Must have clicked another button
				return;
			}

			if (typeof newProps === "string") {
				showNotification({
					type: "error",
					text: newProps,
				});
			} else {
				setState({
					status: "loaded",
					props: newProps,
					show: true,
				});
			}
		},
		props,
	};
};
